import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId?: string;
  groupEventId?: string;
  practitionerId: string;
  practitionerName: string;
  clientId: string;
}

interface ExistingReview {
  id: number;
  rating: number;
  comment: string | null;
  anonymous: boolean;
  created_at: string;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  bookingId,
  groupEventId,
  practitionerId,
  practitionerName,
  clientId,
}) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState<ExistingReview | null>(null);
  const [isLoadingReview, setIsLoadingReview] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkExistingReview = async () => {
      if (!isOpen) return;
      setIsLoadingReview(true);
      try {
        let query = supabase
          .from('reviews')
          .select('id, rating, comment, anonymous, created_at')
          .eq('client_id', clientId);

        if (bookingId) {
          query = query.eq('booking_id', bookingId);
        } else if (groupEventId) {
          query = query.eq('group_event_id', groupEventId);
        }

        const { data, error } = await query.maybeSingle();
        if (error) console.error('Error checking for existing review:', error);
        else if (data) setExistingReview(data);
      } catch (error) {
        console.error('Error checking for existing review:', error);
      } finally {
        setIsLoadingReview(false);
      }
    };
    checkExistingReview();
  }, [isOpen, bookingId, groupEventId, clientId]);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({ title: 'Rating Required', description: 'Please select a rating before submitting.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      const insertData: any = {
        practitioner_id: practitionerId,
        client_id: clientId,
        rating,
        comment: comment.trim() || null,
        anonymous,
        is_published: true,
      };

      if (bookingId) insertData.booking_id = bookingId;
      if (groupEventId) insertData.group_event_id = groupEventId;

      const { error } = await supabase.from('reviews').insert(insertData);
      if (error) throw error;

      toast({ title: 'Review Submitted', description: 'Thank you for your feedback!' });
      setRating(0);
      setComment('');
      setAnonymous(false);
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({ title: 'Submission Failed', description: 'Failed to submit your review. Please try again.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{existingReview ? 'Your Review' : 'Review Your Session'}</DialogTitle>
          <DialogDescription>
            {existingReview ? `Your review for ${practitionerName}` : `Share your experience with ${practitionerName}`}
          </DialogDescription>
        </DialogHeader>

        {isLoadingReview ? (
          <div className="py-8 text-center text-muted-foreground">Loading...</div>
        ) : existingReview ? (
          <ExistingReviewView review={existingReview} />
        ) : (
          <ReviewForm
            rating={rating}
            setRating={setRating}
            hoveredRating={hoveredRating}
            setHoveredRating={setHoveredRating}
            comment={comment}
            setComment={setComment}
            anonymous={anonymous}
            setAnonymous={setAnonymous}
          />
        )}

        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {existingReview ? 'Close' : 'Cancel'}
          </Button>
          {!existingReview && (
            <Button onClick={handleSubmit} disabled={isSubmitting || rating === 0}>
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

function ExistingReviewView({ review }: { review: ExistingReview }) {
  return (
    <div className="space-y-6 py-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Rating</label>
        <div className="flex items-center space-x-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} className={`h-8 w-8 ${star <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />
          ))}
          <span className="ml-2 text-sm text-muted-foreground">{review.rating} star{review.rating !== 1 ? 's' : ''}</span>
        </div>
      </div>
      {review.comment && (
        <div className="space-y-2">
          <label className="text-sm font-medium">Your Comment</label>
          <div className="rounded-md border bg-muted/30 p-3 text-sm">{review.comment}</div>
        </div>
      )}
      {review.anonymous && <div className="text-xs text-muted-foreground">Posted anonymously</div>}
      <div className="text-xs text-muted-foreground">Submitted on {new Date(review.created_at).toLocaleDateString()}</div>
    </div>
  );
}

function ReviewForm({ rating, setRating, hoveredRating, setHoveredRating, comment, setComment, anonymous, setAnonymous }: {
  rating: number; setRating: (v: number) => void;
  hoveredRating: number; setHoveredRating: (v: number) => void;
  comment: string; setComment: (v: string) => void;
  anonymous: boolean; setAnonymous: (v: boolean) => void;
}) {
  return (
    <div className="space-y-6 py-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Rating *</label>
        <div className="flex items-center space-x-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="focus:outline-none transition-transform hover:scale-110"
            >
              <Star className={`h-8 w-8 ${star <= (hoveredRating || rating) ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />
            </button>
          ))}
          <span className="ml-2 text-sm text-muted-foreground">
            {rating > 0 ? `${rating} star${rating !== 1 ? 's' : ''}` : 'Select a rating'}
          </span>
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Comment (optional)</label>
        <Textarea placeholder="Share your experience..." value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={1000} />
        <p className="text-xs text-muted-foreground">{comment.length}/1000 characters</p>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox id="anonymous" checked={anonymous} onCheckedChange={(checked) => setAnonymous(checked as boolean)} />
        <label htmlFor="anonymous" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          Post anonymously
        </label>
      </div>
    </div>
  );
}
